// 채팅(설계서 §9.6) — 세션 화면이 쓰는 훅. 대화 목록·미읽음·보내기·이미지 붙여넣기·대화 저장이 여기 모인다.
//
// 🔴 이력은 **세션 한정**이다(사용자 결정). 세션이 끝나거나 탭을 옮기면(= 재접속) 사라지고, 서버·DB 에는
//    아무것도 남지 않는다. 남기고 싶으면 사람이 [대화 저장]으로 텍스트 파일을 받는다.

import { saveFileToDisk, saveNoticeOf, type SaveNotice } from '../download';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Session } from '../sessions/types';
import { FEATURE, featureAllowed } from '../features';
import { blobToPng, imageFileFromDataTransfer } from '../clipboard/local';
import type { ChatEntry } from './channel';
import { CHAT_ERR_MESSAGE, ChatError, checkText, textBytes } from './protocol';

/**
 * 붙여넣었지만 **아직 보내지 않은** 이미지(설계서 §9.6.7).
 *
 * 🔴 붙여넣는 즉시 보내지 않는다 — 2026-09-23 사용자 보고. 사람은 "첨부하고 확인한 뒤 보낸다" 를 기대하고,
 *    잘못 복사한 그림이 되돌릴 수 없이 상대 화면에 떠 버리는 것은 놀라운 동작이다.
 */
export interface PendingImage {
  /** 보낼 PNG 바이트. */
  png: Uint8Array;
  /** 미리보기용 blob URL(보내거나 취소할 때 반드시 놓아 준다). */
  url: string;
}

export interface Chat {
  /** 채팅을 쓸 수 있는가(에이전트가 채널을 열었는가). false 면 단추 자체를 숨긴다. */
  available: boolean;
  /**
   * 글을 보낼 수 있는가. 채널은 있는데 이것이 false 면 **상대에게 표시할 화면이 없다**(`ui:'none'`) —
   * 화면은 입력을 잠그고 `blockedReason` 을 보여야 한다.
   */
  canSend: boolean;
  /** 잠긴 이유(사용자 문구). `canSend` 가 true 면 undefined. */
  blockedReason?: string;
  /** 이미지를 보낼 수 있는가(상대 `hello.kinds`). false 면 붙여넣기를 막는다. */
  canSendImage: boolean;
  /** 이 세션의 대화(오래된 것부터). */
  entries: ChatEntry[];
  /** 패널이 열려 있는가. */
  open: boolean;
  setOpen(open: boolean): void;
  /** 패널이 닫혀 있는 동안 받은 대화 수(단추에 표시). 패널을 열면 0. */
  unread: number;
  /**
   * 상대의 글이 와서 **저절로** 열렸는가(2026-09-29). true 면 화면은 입력 포커스를 가져가지 않는다 —
   * 🔴 담당자가 원격 화면에 글을 치는 중이면 그 키 입력이 채팅 입력칸으로 샌다. 사람이 열면 false.
   */
  openedQuietly: boolean;
  /** 보내기 — 첨부한 이미지가 있으면 먼저 보내고, 글이 있으면 이어서 보낸다. */
  send(text: string): void;
  /**
   * 붙여넣기에서 이미지를 **첨부**한다(보내지는 않는다 — [보내기]/Enter 가 보낸다).
   * 이미지를 집었으면 `true` → 호출부가 **동기로** `preventDefault()` 해야 한다.
   * 이미지가 없으면 `false` → 호출부는 기본 붙여넣기(글자 넣기)를 이어간다.
   */
  attach(dt: DataTransfer | null): boolean;
  /** 첨부 대기 중인 이미지(미리보기). 없으면 undefined. */
  pending?: PendingImage;
  /** 첨부를 취소한다. */
  clearAttachment(): void;
  /** 마지막 오류 문구(보내기 실패·거절). */
  error?: string;
  clearError(): void;
  /** 대화를 텍스트 파일로 내려받는다(세션 한정 이력의 유일한 보존 수단). */
  download(): void;
  /**
   * 저장 안내(성공이면 경로·[폴더 열기]). 🔴 성공도 알린다 — 2026-09-24 Mac 실기에서 파일은 생겼는데
   * 화면에 아무 표시가 없어 "반응 없음" 으로 보고됐다.
   */
  notice?: SaveNotice;
  clearNotice(): void;
  /**
   * 글·그림을 **바로** 보낸다(첨부 대기를 거치지 않는다). 채팅 별도 창(§9.6.6)이 자기 창에서 첨부한 그림을
   * 세션 창으로 넘겨 보낼 때 쓴다 — 첨부 확인은 그 창에서 이미 끝났다.
   */
  sendRaw(text: string, png?: Uint8Array): void;
  /** 입력칸 초안(탭을 옮겨도 남게 훅이 들고 있다). */
  draft: string;
  setDraft(text: string): void;
}

/** 내려받는 파일 이름에 쓰는 시각(로컬 기준 `20260921-1435`). */
function stamp(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
}

/** 대화를 사람이 읽을 텍스트로 — 시각 · 보낸 쪽 · 내용(이미지는 표시만). */
export function formatTranscript(entries: ChatEntry[], at: Date): string {
  const head = `원격지원 대화 기록 — ${at.toLocaleString()}`;
  const lines = entries.map((e) => {
    const t = new Date(e.at).toLocaleTimeString();
    const who = e.from === 'me' ? '나' : '상대';
    const body = e.kind === 'image' ? '[이미지]' : (e.text ?? '');
    return `[${t}] ${who}: ${body}`;
  });
  // 끝에 줄바꿈 하나 — 텍스트 파일 관례.
  return [head, '─'.repeat(40), ...lines, ''].join('\n');
}

/**
 * @param session  세션
 * @param autoOpen 닫혀 있는 동안 상대의 글이 오면 **저절로 열지**(2026-09-29 사용자 결정 — 원격지와 같은 동작).
 *                 false 면 종전처럼 단추 배지만 늘린다(브라우저의 별도 창 모드 — `window.open` 은 포커스를 빼앗아서).
 */
export function useChat(session: Session, autoOpen = true): Chat {
  const channel = session.client.chat;
  const caps = session.chatCaps;
  // 기능 권한(§14.19.2) `chat` 이 없으면 숨긴다 — 새 에이전트는 채널을 만들지 않고, 구버전 에이전트 기간엔 여기서 숨긴다.
  const available = !!caps && !!channel && featureAllowed(session.features, FEATURE.CHAT);
  // 🔴 제어 권한(`controlAllowed`)을 보지 **않는다** — 채팅은 조작이 아니라 말 걸기라 보기 전용 세션에도 된다(§9.6).
  const canSend = available && caps.ui === 'app';
  const canSendImage = canSend && (channel?.canSendImage ?? false);

  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [open, setOpenState] = useState(false);
  const [unread, setUnread] = useState(0);
  const [openedQuietly, setOpenedQuietly] = useState(false);
  const autoOpenRef = useRef(autoOpen);
  autoOpenRef.current = autoOpen;
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<SaveNotice | undefined>(undefined);
  const [draft, setDraft] = useState('');
  /** 붙여넣었지만 아직 보내지 않은 이미지(§9.6.7). */
  const [pending, setPending] = useState<PendingImage | undefined>(undefined);
  // `send` 가 최신 첨부를 보게 한다(의존성에 넣으면 첨부할 때마다 보내기 함수가 새로 만들어진다).
  const pendingRef = useRef<PendingImage | undefined>(undefined);
  pendingRef.current = pending;
  // 패널 열림 여부를 리스너(한 번 등록)가 최신 값으로 보게 한다.
  const openRef = useRef(open);
  openRef.current = open;

  // 대화 수신 — 화면이 채널에 직접 리스너를 단다(매니저 콜백에는 닿지 못한다).
  useEffect(() => {
    if (!channel) {
      return;
    }
    channel.setListener({
      onEntry: (entry) => {
        setEntries((prev) => [...prev, entry]);
        if (!openRef.current) {
          // 🔴 닫아 둔 채 상대의 글이 오면 **연다**(2026-09-29 사용자 보고 — 배지만 늘어 "반응이 없다" 로 보였다).
          //    포커스는 가져가지 않는다(`openedQuietly`). 저절로 열 수 없는 모드면 종전처럼 배지만.
          if (autoOpenRef.current && entry.from !== 'me') {
            openRef.current = true; // 같은 틱에 여러 줄이 와도 한 번만 연다
            setOpenedQuietly(true);
            setOpenState(true);
          } else {
            setUnread((n) => n + 1);
          }
        }
      },
      onAck: (id, ok, message) => {
        setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, delivered: ok, error: ok ? undefined : message } : e)));
        if (!ok && message) {
          setError(message);
        }
      },
    });
    return () => channel.setListener(null);
  }, [channel]);

  // 이미지 blob: URL 정리 — 세션이 끝나면 모두 놓는다(놓지 않으면 탭을 닫아도 메모리에 남는다).
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  useEffect(
    () => () => {
      for (const e of entriesRef.current) {
        if (e.imageUrl) {
          URL.revokeObjectURL(e.imageUrl);
        }
      }
    },
    [],
  );

  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
    setOpenedQuietly(false); // 사람이 여닫았다 — 열면 입력칸에 포커스를 줘도 된다
    if (next) {
      setUnread(0);
    }
  }, []);

  /**
   * 보내기의 본체 — 그림(있으면) 먼저, 이어서 글.
   * @param image 보낼 그림과 그 미리보기 URL(말풍선이 이어받는다 — 여기서 놓으면 방금 보낸 그림이 깨진다)
   */
  const sendParts = useCallback(
    (text: string, image?: PendingImage) => {
      if (!channel || !caps || !canSend) {
        return;
      }
      // ① 첨부한 이미지를 **먼저** 보낸다(사람이 붙인 순서대로 보이게 — 글은 대개 그림 설명이다).
      if (image) {
        const imgId = channel.sendImage(image.png);
        void imgId.then((id) =>
          setEntries((prev) => [...prev, { id, from: 'me', kind: 'image', imageUrl: image.url, at: Date.now() }]),
        );
      }
      // ② 글은 있을 때만. 그림만 보내는 것도 정상이다.
      if (text) {
        // NFC 로 — Mac 에서 붙여 넣은 경로 등은 한글이 자모로 풀려 있다(clipboard/local.ts `textContent` 와 같은 이유, 2026-10-23).
        const body = text.normalize('NFC');
        try {
          checkText(body, caps);
        } catch (e) {
          setError(e instanceof ChatError ? e.message : '보내지 못했습니다.');
          return;
        }
        const id = channel.sendText(body);
        setEntries((prev) => [...prev, { id, from: 'me', kind: 'text', text: body, at: Date.now() }]);
      }
      setDraft('');
      setError(undefined);
    },
    [channel, caps, canSend],
  );

  const send = useCallback(
    (text: string) => {
      const attached = pendingRef.current;
      if (attached) {
        setPending(undefined); // URL 은 말풍선이 이어받는다(놓지 않는다)
      }
      sendParts(text, attached);
    },
    [sendParts],
  );

  const sendRaw = useCallback(
    (text: string, png?: Uint8Array) => {
      const image = png
        ? { png, url: URL.createObjectURL(new Blob([png as unknown as BlobPart], { type: 'image/png' })) }
        : undefined;
      sendParts(text, image);
    },
    [sendParts],
  );

  /** 첨부 대기 이미지를 놓아 준다(미리보기 URL 회수). 보낼 때는 말풍선이 URL 을 이어받으므로 **놓지 않는다**. */
  const clearAttachment = useCallback(() => {
    setPending((cur) => {
      if (cur) {
        URL.revokeObjectURL(cur.url);
      }
      return undefined;
    });
  }, []);

  /**
   * 붙여넣기 → **첨부**(보내지 않는다). 판정은 동기, PNG 변환만 비동기다.
   *
   * 🔴 동기 판정이 핵심이다 — `await` 뒤에서 `preventDefault()` 를 부르면 이미 늦어
   *    기본 붙여넣기가 끝나 있다(macOS 스크린샷의 **파일 이름**이 입력칸에 들어가 그대로 전송됐다).
   */
  const attach = useCallback(
    (dt: DataTransfer | null): boolean => {
      if (!channel || !caps) {
        return false;
      }
      const file = imageFileFromDataTransfer(dt);
      if (!file) {
        return false; // 이미지가 없다 → 호출부가 기본 붙여넣기를 이어간다
      }
      if (!canSendImage) {
        setError(CHAT_ERR_MESSAGE.unsupported ?? '상대 기기는 이미지를 받을 수 없습니다.');
        return true; // 기본 동작으로 넘기지 않는다 — 넘기면 원격 화면에 붙여넣기가 된다
      }
      void blobToPng(file)
        .then((png) => {
          if (png.length > caps.maxImage) {
            setError(CHAT_ERR_MESSAGE.too_large);
            return;
          }
          const url = URL.createObjectURL(new Blob([png as unknown as BlobPart], { type: 'image/png' }));
          // 앞서 첨부해 둔 것이 있으면 놓아 주고 바꾼다(한 번에 하나만 붙인다).
          setPending((cur) => {
            if (cur) {
              URL.revokeObjectURL(cur.url);
            }
            return { png, url };
          });
          setError(undefined);
        })
        .catch(() => setError('이미지를 읽지 못했습니다.'));
      return true;
    },
    [channel, caps, canSendImage],
  );

  const download = useCallback(() => {
    const now = new Date();
    const text = formatTranscript(entriesRef.current, now);
    // 🔴 저장은 `saveFileToDisk` 한 곳으로 한다. 종전에는 여기서 바로 `<a download>` 를 썼는데,
    //    **통합앱(WebView2)에서는 그것이 조용히 아무 일도 하지 않는다**(2026-09-23 앱 실기에서 확인) —
    //    즉 앱에서는 [대화 저장]이 한 번도 동작한 적이 없었다(오류가 없어 아무도 몰랐다).
    void saveFileToDisk(`원격지원-대화-${stamp(now)}.txt`, text, 'text/plain;charset=utf-8').then((r) =>
      setNotice(saveNoticeOf('대화를', r)),
    );
  }, []);

  const blockedReason = useMemo(() => {
    if (!available || canSend) {
      return undefined;
    }
    return '상대 기기에 채팅을 표시할 화면이 없습니다(통합앱이 설치돼 있지 않습니다).';
  }, [available, canSend]);

  return {
    available,
    canSend,
    blockedReason,
    canSendImage,
    entries,
    open,
    setOpen,
    unread,
    openedQuietly,
    send,
    attach,
    pending,
    clearAttachment,
    error,
    clearError: useCallback(() => setError(undefined), []),
    download,
    notice,
    clearNotice: useCallback(() => setNotice(undefined), []),
    sendRaw,
    draft,
    setDraft,
  };
}

/** 입력칸 도움 문구용 — 남은 글자(바이트) 여유. */
export function remainingBytes(text: string, max: number): number {
  return max - textBytes(text);
}
